import { IOperationResultDTO } from "coreframetypings/dto";
import ComponentManager from '@/Management/ComponentManager';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import RADApplication from '@/Management/RADApplication';
import PasswordBoxViewModel from '@/ViewModels/Controls/PasswordBoxViewModel';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import LabelViewModelBuilder from '@/Builder/LabelViewModelBuilder';
import Utilities from '@/Utility/Utilities';
import ResourceManager from '@/Management/ResourceManager';
import TwoFactorPopupViewModel from '@/Components/TwoFaktorAuth/PopupWindow/TwoFactorPopupViewModel';
import TwoFactorTokenDialogViewFactory from '@/Components/TwoFaktorAuth/TokenDialog/TwoFactorTokenDialogViewFactory';
import NavigationService from '@/Management/NavigationService';

export default class TwoFactorSelfServiceViewViewModel extends TwoFactorPopupViewModel {

    public passwordLabel: LabelViewModel;
    public passwordBox: PasswordBoxViewModel;
    
    public showPassword: KnockoutObservable<boolean>;

    public constructor(protected params: any) {
        super($.extend(params, {
            isOpen: true,
        }));
        
        this.onOpen = () => {
            this.generateToken(false);
        }

        this.passwordBox = this.createPasswordBoxViewModel();
        this.passwordLabel = this.createPasswordLabelViewModel();
        this.showPassword = this.observableOrDefault(false);
        
        this.twoFactorViewModel.resetButton.onClick = (): void => {
            this.generateToken(true);
        }
    }

    private createPasswordBoxViewModel(): PasswordBoxViewModel {
        const vm = new PasswordBoxViewModel({
            parentLocator: this.locator,
            locator: 'password',
        });
        this.defineSubscription(vm.value, (value) => {
            if (Utilities.isNullOrUndefinedOrBlank(value)) {
                vm.isValid(false);
                vm.invalidationText(ResourceManager.getInstance().get('REQUIRED_CONSTRAINT'))
                return;
            }
            vm.isValid(true);
            vm.invalidationText('');
        })();

        return vm;
    }

    private createPasswordLabelViewModel(): LabelViewModel {
        return new LabelViewModelBuilder()
            .withErgName('USER_PASSWORD')
            .build();
    }

    /**
     * @override
     */
    protected getUsername(): string {
        return RADApplication.getInstance().user.username();
    }

    private generateToken(update: boolean): void {
        if ((!this.showPassword() || !this.passwordBox.isValid()) && update) {
            this.showPassword(true);
            return;
        }

        this.increaseBusyCounter();
        this.callGenerateToken(update).then((result) => {
            if (this.validateStatus(result)) {
                const tokenDTO = result.returnValue;
                const tokenMissing = tokenDTO.TokenMissing;
                if (tokenMissing) {
                    this.openTwoFATokenDialog();
                    return;
                }
                const token = tokenDTO.Token;
                if (!Utilities.isNullOrUndefinedOrBlank(token)) {
                    this.twoFactorViewModel.token(token);
                    this.generateTokenCallback(update);
                }
            }
        }).always(() => {
            if (this.isDisposed)
                return
            this.decreaseBusyCounter()
        });
    }

    private openTwoFATokenDialog(): void {
        let tokenDialogViewFactory: TwoFactorTokenDialogViewFactory = NavigationService.getInstance().loadViewFactoryIsolated('TwoFactorTokenDialogView', {
            infoTextErgName: 'MENU_CHANGE_PASSWORD_TWO_FA_INFO',
            buttonErgname: 'MENU_CHANGE_PASSWORD_TWO_FA_BUTTON',
            shouldCleanRegion: false,
            username: RADApplication.getInstance().user.username(),
            onClose: () => {
                NavigationService.getInstance().removeViewFactoryIsolated(tokenDialogViewFactory);
            },
            onSubmit: (token) => {
                return this.callGenerateToken(true, token).then((result) => {
                    if (this.validateStatus(result) && result.returnValue) {
                        const token = result.returnValue.Token;
                        if (!Utilities.isNullOrUndefinedOrBlank(token)) {
                            this.twoFactorViewModel.token(token);
                            this.generateTokenCallback(true);
                        }
                    }
                });
            }
        }, true);
    }

    private callGenerateToken(update: boolean, token?: string): JQueryPromise<IOperationResultDTO> {
        const passwordContainsToken = !Utilities.isNullOrUndefinedOrBlank(token);
        const password = passwordContainsToken ? this.passwordBox.value() + token : this.passwordBox.value();
        return RADEnvironmentProxy.getInstance().callStaticOperation({
            pdTypeName: 'RAD.RADFoundation.ProblemDomain.User',
            operationName: 'GenerateTwoFactorToken',
            operationParameters: [update, password, passwordContainsToken],
            contextParameters: {}
        }).promise();
    }

    private generateTokenCallback(update: boolean): void {
        this.showPassword(false);
        this.passwordBox.resetValue();
    }
}

ComponentManager.setViewModel('TwoFactorSelfServiceViewViewModel', TwoFactorSelfServiceViewViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');